import { useEffect, useMemo } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ThemeProvider, CssBaseline, useMediaQuery } from '@mui/material'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { lightTheme, darkTheme, withPrimaryColor } from './theme'
import { queryClient } from './lib/queryClient'
import { useAuthStore } from './store/authStore'
import { useThemeStore } from './store/themeStore'
import { useAppColorStore } from './store/appColorStore'
import { api } from './lib/api'
import { ApiError } from '@cmms/api-client'

import { LoginPage } from './features/auth/LoginPage'
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './features/auth/ResetPasswordPage'
import { RegisterOrgPage } from './features/auth/RegisterOrgPage'
import { AcceptInvitePage } from './features/auth/AcceptInvitePage'
import { PublicReportIssuePage } from './features/public-report/PublicReportIssuePage'
import { ChatSocketProvider } from './hooks/useChatSocket'
import { ChecklistsPage } from './features/checklists/ChecklistsPage'
import { ChatNotificationHost } from './components/ChatNotificationHost'
import { ProtectedRoute } from './features/auth/ProtectedRoute'
import { AppShell } from './layouts/AppShell'
import { OverviewPage } from './features/overview/OverviewPage'
// UsersPage kept in features/users/ for backend-wired settings use, but
// the main /users route now redirects to /people.
// import { UsersPage } from './features/users/UsersPage'
import { WorkOrdersPage } from './features/work-orders/WorkOrdersPage'
import { TasksPage } from './features/tasks/TasksPage'
import { RequestsPage } from './features/requests/RequestsPage'
import { LocationsPage } from './features/locations/LocationsPage'
import { AssetsPage } from './features/assets/AssetsPage'
import { PeoplePage } from './features/people/PeoplePage'
import { InventoryPage } from './features/inventory/InventoryPage'
import { VendorsPage } from './features/vendors/VendorsPage'
import { WorkflowsPage } from './features/workflows/WorkflowsPage'
import { ReportsPage } from './features/reports/ReportsPage'
import { CategoriesPage } from './features/asset-categories/CategoriesPage'
import { SettingsPage } from './features/settings/SettingsPage'
import { ChatsPage } from './features/chats/ChatsPage'
import { HelpPage } from './features/help/HelpPage'
import { ConfirmDialogProvider } from './components/ConfirmDialog'
import { SnackbarHost } from './components/SnackbarHost'

function HomeRedirect() {
  const accessToken = useAuthStore((s) => s.accessToken)
  return <Navigate to={accessToken ? '/overview' : '/login'} replace />
}

export default function App() {
  const themeMode = useThemeStore((s) => s.mode)
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const accessToken = useAuthStore((s) => s.accessToken)
  const isHydrated = useAuthStore((s) => s.isHydrated)
  const setUser = useAuthStore((s) => s.setUser)
  const clear = useAuthStore((s) => s.clear)

  const userColor = useAppColorStore((s) => s.primaryColor)

  const theme = useMemo(() => {
    const useDark = themeMode === 'dark' || (themeMode === 'system' && prefersDark)
    const base = useDark ? darkTheme : lightTheme
    return userColor ? withPrimaryColor(base, userColor) : base
  }, [themeMode, prefersDark, userColor])

  const setAppColor = useAppColorStore((s) => s.setPrimaryColor)

  useEffect(() => {
    if (!isHydrated || !accessToken) return
    api.auth
      .me()
      .then((user) => {
        setUser(user)
        // Sync user's saved color preference on login
        api.users.getProfile().then((profile) => {
          const savedColor = (profile as any)?.preferences?.primaryColor
          if (savedColor && typeof savedColor === 'string') {
            setAppColor(savedColor)
          }
        }).catch(() => {})
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) clear()
      })
  }, [isHydrated, accessToken, setUser, clear, setAppColor])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarHost />
        <ConfirmDialogProvider>
        <ChatSocketProvider>
        <BrowserRouter>
          <ChatNotificationHost />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register-org" element={<RegisterOrgPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/invitations/accept" element={<AcceptInvitePage />} />
            {/* Public location-QR "Report an issue" flow. Renders outside
                the AppShell so unauthenticated visitors don't hit the
                sidebar chrome. The page itself redirects signed-in users
                to the internal location detail. */}
            <Route path="/report/:orgSlug/:siteId" element={<PublicReportIssuePage />} />
            <Route path="/report/:orgSlug/:siteId/:locationId" element={<PublicReportIssuePage />} />
            {/* Path-based workspace slug — shareable login URL like
                /bacsys-demo/login so customers don't type the slug. Must
                come AFTER the other static paths so they win the match. */}
            <Route path="/:slug/login" element={<LoginPage />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                <Route path="/overview" element={<OverviewPage />} />
                <Route path="/overview/detailed" element={<OverviewPage />} />
                <Route path="/overview/checklists" element={<OverviewPage />} />
                <Route path="/overview/tickets" element={<OverviewPage />} />
                <Route path="/overview/my" element={<OverviewPage />} />
                <Route path="/overview/feedback" element={<OverviewPage />} />

                {/* Real, wired-up pages */}
                <Route path="/locations"             element={<LocationsPage />} />
                <Route path="/locations/:locationId" element={<LocationsPage />} />
                <Route path="/people"           element={<PeoplePage />} />
                <Route path="/people/:userId"  element={<PeoplePage />} />
                <Route path="/settings"         element={<SettingsPage />} />
                <Route path="/settings/sites"   element={<SettingsPage />} />
                <Route path="/settings/sites/:siteId" element={<SettingsPage />} />

                <Route path="/work-orders" element={<WorkOrdersPage />} />
                <Route path="/work-orders/:workOrderId" element={<WorkOrdersPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/tasks/:taskId" element={<TasksPage />} />

                {/* Still placeholders — will be wired as epics ship */}
                <Route path="/requests" element={<RequestsPage />} />
                <Route path="/requests/:requestId" element={<RequestsPage />} />
                <Route path="/checklists" element={<ChecklistsPage />} />
                <Route path="/checklists/:checklistId" element={<ChecklistsPage />} />
                <Route path="/workflows" element={<WorkflowsPage />} />
                <Route path="/assets" element={<AssetsPage />} />
                <Route path="/assets/:assetId" element={<AssetsPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/categories/:categoryId" element={<CategoriesPage />} />
                <Route path="/vendors" element={<VendorsPage />} />
                <Route path="/vendors/:vendorId" element={<VendorsPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/inventory/:partId" element={<InventoryPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/chats" element={<ChatsPage />} />
                <Route path="/chats/:conversationId" element={<ChatsPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/subscription" element={<Navigate to="/settings?tab=subscription" replace />} />

                {/* Aliases / backward-compat redirects */}
                <Route path="/sites"     element={<Navigate to="/locations" replace />} />
                <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
                <Route path="/users"     element={<Navigate to="/people" replace />} />
                <Route path="/teams"     element={<Navigate to="/people" replace />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ChatSocketProvider>
        </ConfirmDialogProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </ThemeProvider>
    </QueryClientProvider>
  )
}
